import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { ComparisonBarChartComponent as ParentComparisonBarChartComponent } from '@shared-lib/chart-components/comparison-bar-chart/comparison-bar-chart.component';
import { ConfigService } from '@shared-lib/shared-services/config.service';
import { GlobalFilterService } from '@shared-lib/shared-services/global-filter-service';
import { applyRoleVisibilityToOption } from '@shared-lib/utils/chart-role-visibility.utils';
import { Subject, takeUntil } from 'rxjs';

type SplitComparisonChartView = {
  key: string;
  label: string;
  sourceIndexes: number[];
  values: number[];
  comparisons: Array<number | null>;
  renderableBarFlags: boolean[];
  maxValue: number;
  baselineIndex: number;
};

@Component({
  selector: 'app-comparison-bar-chart',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './comparison-bar-chart.component.html',
  styleUrls: ['./comparison-bar-chart.component.scss']
})
export class ComparisonBarChartComponent extends ParentComparisonBarChartComponent {
  private readonly lccNoBreakdownMineWhitelist = new Set<string>([
    'antapaccay',
    'collahuasi',
    'sierra gorda',
    'penasquito',
    'north antelope rochelle mine (narm)',
    'fort hills'
  ]);
  private formulaResult: number | null = null;
  private useFormulaComparisonFallback = false;
  private readonly defaultChartScale = 1;
  private readonly defaultBarGap = 2;
  private readonly baseTopPadding = 30;
  private readonly connectorHeaderClearance = 6;
  private readonly connectorLabelZoneHeight = 20;
  private readonly connectorValueZoneHeight = 20;
  private readonly customConnectorGap = 3;
  private readonly connectorLevelStep = 16;
  private readonly connectorTipClearance = -37;
  private renderableBarFlags: boolean[] = [];
  private stackSegmentsByBar: number[][] = [];
  private stackEnabledByBar: boolean[] = [];
  splitComparisonCharts: SplitComparisonChartView[] = [];
  private customDestroy$ = new Subject<void>();
  private lastRawStackSignature = '';
  barLabelsWithoutBreakdown: Set<string> = new Set();
  private activeRegion = '';

  constructor(
    private customConfigService: ConfigService,
    private customGlobalFilterService: GlobalFilterService
  ) {
    super(customConfigService, customGlobalFilterService);
    this.activeRegion = this.resolveRegion(this.customGlobalFilterService.getCurrentFilters());

    this.customGlobalFilterService.filter$
      .pipe(takeUntil(this.customDestroy$))
      .subscribe((filters) => {
        this.activeRegion = this.resolveRegion(filters);
      });
  }

  override set data(value: any) {
    super.data = this.applyRoleVisibilityToData(value);
    this.activeRegion = this.resolveRegion(this.customGlobalFilterService.getCurrentFilters());
    this.loadRawStackData();
  }

  override get data(): any {
    return super.data;
  }

  private applyRoleVisibilityToData(value: any): any {
    if (!value) {
      return value;
    }

    const labelsVisible = value?.chartConfig?.showLabels !== false;
    const dataLabelsVisible = value?.chartConfig?.showDataLabels !== false;
    const tooltipVisible = value?.chartConfig?.tooltip?.show !== false && value?.tooltip !== false;
    const option: any = {
      tooltip: { show: tooltipVisible },
      xAxis: { axisLabel: { show: labelsVisible } },
      yAxis: { axisLabel: { show: labelsVisible } },
      series: [{ label: { show: dataLabelsVisible } }]
    };

    applyRoleVisibilityToOption(option, value);

    const hideTooltip = this.hasHiddenTooltip(option.tooltip);
    const hideAxisLabels = this.hasHiddenAxisLabels(option.xAxis) || this.hasHiddenAxisLabels(option.yAxis);
    const hideDataLabels = hideAxisLabels || this.hasHiddenSeriesLabels(option.series);

    if (!hideTooltip && !hideAxisLabels && !hideDataLabels) {
      return value;
    }

    const chartConfig = {
      ...(value.chartConfig || {}),
      tooltip: {
        ...(value.chartConfig?.tooltip || {})
      }
    };
    const next = {
      ...value,
      chartConfig
    };

    if (hideTooltip) {
      chartConfig.tooltip.show = false;
      next.tooltip = false;
    }

    if (hideAxisLabels) {
      chartConfig.showLabels = false;
      next.showLabels = false;
    }

    if (hideDataLabels) {
      chartConfig.showDataLabels = false;
      chartConfig.showBarLabels = false;
      chartConfig.showLineLabels = false;
    }

    return next;
  }

  private hasHiddenTooltip(tooltip: any): boolean {
    if (Array.isArray(tooltip)) {
      return tooltip.some((item) => this.hasHiddenTooltip(item));
    }

    return tooltip?.show === false;
  }

  private hasHiddenAxisLabels(axis: any): boolean {
    if (Array.isArray(axis)) {
      return axis.some((item) => this.hasHiddenAxisLabels(item));
    }

    return axis?.axisLabel?.show === false;
  }

  private hasHiddenSeriesLabels(series: any): boolean {
    if (Array.isArray(series)) {
      return series.some((item) => this.hasHiddenSeriesLabels(item));
    }

    return series?.label?.show === false;
  }

  override getBarColor(index: number): string {
    const label = (this.labels && this.labels[index]) ? this.labels[index].trim() : '';
    const region = this.resolveRegion(this.customGlobalFilterService.getCurrentFilters());
    const overrides = this.data?.legendOverrides?.[region];
    const isChinaRegion = region === 'CHINA';

    if (label) {
      const overrideColor = this.findLegendColorByLabel(label, overrides, false);
      if (overrideColor) {
        return overrideColor;
      }

      if (isChinaRegion) {
        const baseColor = this.findLegendColorByLabel(label, this.data?.legends, true);
        if (baseColor) {
          return baseColor;
        }
      }
    }

    if (Array.isArray(overrides) && overrides.length > 0) {
      // Keep index-based fallback for non-CHINA regions.
      // For CHINA we must color strictly by response labels from API/config.
      if (!isChinaRegion && overrides[index]?.color) {
        return overrides[index].color;
      }
    }

    return super.getBarColor(index);
  }

  private resolveRegion(filters?: any): string {
    const navState = (window?.history?.state || {}) as any;
    const requestParams = filters?.requestParams;
    const queryParams = filters?.queryParams;
    const fromFilters = this.normalizeRegion(
      requestParams?.region ??
      queryParams?.region ??
      filters?.region ??
      filters?.Region ??
      filters?.selectedRegion ??
      filters?.selected_region
    );
    if (fromFilters) {
      return fromFilters;
    }

    const fromState = this.normalizeRegion(
      navState?.selectedRegion ??
      navState?.region ??
      navState?.selected_region ??
      navState?.selectedMine?.region ??
      navState?.selectedMine?.__regionRaw ??
      navState?.selectedMine?.__regionNorm
    );
    if (fromState) {
      return fromState;
    }

    return this.normalizeRegion(this.activeRegion);
  }

  private normalizeRegion(rawRegion: any): string {
    let value = rawRegion;

    while (Array.isArray(value) && value.length > 0) {
      value = value[0];
    }

    if (value && typeof value === 'object') {
      value = value.value ?? value.key ?? value.id ?? value.label ?? '';
    }

    return `${value ?? ''}`.trim().toUpperCase();
  }

  private normalizeLegendToken(label: any): string {
    return `${label ?? ''}`
      .trim()
      .toUpperCase()
      .replace(/[^A-Z0-9 ]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  private splitLegendToken(token: string): { vendor: string; qualifier: string } {
    const normalized = this.normalizeLegendToken(token);
    if (!normalized) {
      return { vendor: '', qualifier: '' };
    }

    const parts = normalized.split(' ').filter((part) => part.length > 0);
    if (parts.length === 1) {
      return { vendor: parts[0], qualifier: '' };
    }

    return {
      vendor: parts.slice(0, parts.length - 1).join(' '),
      qualifier: parts[parts.length - 1]
    };
  }

  private computeEditDistance(left: string, right: string): number {
    if (left === right) {
      return 0;
    }

    const a = left || '';
    const b = right || '';
    const dp: number[][] = Array.from({ length: a.length + 1 }, () => Array(b.length + 1).fill(0));

    for (let i = 0; i <= a.length; i++) {
      dp[i][0] = i;
    }
    for (let j = 0; j <= b.length; j++) {
      dp[0][j] = j;
    }

    for (let i = 1; i <= a.length; i++) {
      for (let j = 1; j <= b.length; j++) {
        const cost = a[i - 1] === b[j - 1] ? 0 : 1;
        dp[i][j] = Math.min(
          dp[i - 1][j] + 1,
          dp[i][j - 1] + 1,
          dp[i - 1][j - 1] + cost
        );
      }
    }

    return dp[a.length][b.length];
  }

  private findLegendColorByLabel(label: string, entries: any, allowFuzzy = true): string | null {
    if (!label || !Array.isArray(entries) || entries.length === 0) {
      return null;
    }

    const targetToken = this.normalizeLegendToken(label);
    if (!targetToken) {
      return null;
    }

    for (const entry of entries) {
      if (!entry || !entry.label || !entry.color) {
        continue;
      }

      if (this.normalizeLegendToken(entry.label) === targetToken) {
        return `${entry.color}`;
      }
    }

    if (!allowFuzzy) {
      return null;
    }

    const targetParts = this.splitLegendToken(targetToken);
    if (!targetParts.vendor || !targetParts.qualifier) {
      return null;
    }

    let bestColor: string | null = null;
    let bestDistance = Number.MAX_SAFE_INTEGER;
    for (const entry of entries) {
      if (!entry || !entry.label || !entry.color) {
        continue;
      }

      const candidateToken = this.normalizeLegendToken(entry.label);
      const candidateParts = this.splitLegendToken(candidateToken);
      if (!candidateParts.vendor || candidateParts.qualifier !== targetParts.qualifier) {
        continue;
      }

      const distance = this.computeEditDistance(targetParts.vendor, candidateParts.vendor);
      if (distance <= 2 && distance < bestDistance) {
        bestDistance = distance;
        bestColor = `${entry.color}`;
      }
    }

    return bestColor;
  }

  override ngOnDestroy(): void {
    this.customDestroy$.next();
    this.customDestroy$.complete();
    super.ngOnDestroy();
  }

  protected override onDataAssigned(): void {
    if (!this.data) {
      return;
    }

    if (this.data.apiUrl) {
      return;
    }

    super.onDataAssigned();
  }

  protected override loadChartData(filters?: any): void {
    if (!this.data) {
      return;
    }

    if (!this.data?.apiUrl) {
      super.loadChartData(filters);
      return;
    }

    this.loadRawStackData(filters);
  }

  override prepareChart(source?: any): void {
    super.prepareChart(source);
    this.formulaResult = null;
    this.useFormulaComparisonFallback = false;
    this.prepareStackedSegments(source);
    this.recomputeRenderableFlags();
    this.rebuildSplitComparisonCharts();
    this.applyFirstAvailableBaselineComparison();

    if (!this.isFormulaModeEnabled()) {
      return;
    }

    // Formula bubble override is only for the equation result chart.
    if (!this.isEquationResult()) {
      return;
    }

    const values = Array.isArray(this.values) ? this.values : [];
    if (values.length < 4) {
      return;
    }

    const formulaConfig = this.resolveFormulaConfig();
    const numeratorTotal = formulaConfig.numeratorIndexes.reduce((sum: number, idx: number) => {
      const value = Number(values[idx] ?? 0);
      return Number.isFinite(value) ? sum + value : sum;
    }, 0);
    const d = Number(values[formulaConfig.denominatorIndex] ?? 0);

    const fallback = this.getFormulaFallbackValue();
    let rounded = fallback;

    if (!this.isFixedFormulaOutputEnabled() && Number.isFinite(d) && d !== 0) {
      const multiplierRaw = Number(this.data?.formulaMultiplier ?? this.data?.formula?.multiplier ?? 1);
      const multiplier = Number.isFinite(multiplierRaw) ? multiplierRaw : 1;
      const result = (numeratorTotal / d) * multiplier;
      rounded = Number.isFinite(result) ? Math.round(result) : fallback;
    }

    this.formulaResult = rounded;

    // In formula mode (result chart), calculate per-bar percentage vs baseline.
    // If baseline is invalid, fall back to formula output value.
    this.useFormulaComparisonFallback = true;
    const baselineIndex = this.getFirstRenderableIndex();
    const baseline = baselineIndex >= 0 ? Number(values[baselineIndex]) : Number.NaN;
    this.comparisons = values.map((value, index) => {
      if (index === baselineIndex || !this.isRenderableValue(value)) {
        return null;
      }

      if (!Number.isFinite(baseline) || baseline === 0) {
        return rounded;
      }

      const current = Number(value);
      const percent = ((current - baseline) / baseline) * 100;
      return Number.isFinite(percent) ? Math.round(percent) : rounded;
    });
  }

  override hasComparison(index: number): boolean {
    if (!this.isRenderableBar(index)) {
      return false;
    }

    if (this.isFormulaModeEnabled() && this.useFormulaComparisonFallback) {
      const baselineIndex = this.getBaselineBarIndex();
      const value = this.comparisons[index];
      return index !== baselineIndex && typeof value === 'number' && Number.isFinite(value);
    }

    return super.hasComparison(index);
  }

  override getBarComparison(index: number): number | null {
    if (!this.isRenderableBar(index)) {
      return null;
    }

    if (this.isFormulaModeEnabled() && this.useFormulaComparisonFallback) {
      const baselineIndex = this.getBaselineBarIndex();
      if (index === baselineIndex) {
        return null;
      }

      const value = this.comparisons[index];
      return typeof value === 'number' && Number.isFinite(value) ? value : null;
    }

    return super.getBarComparison(index);
  }

  override getBarHeight(value: number): number {
    if (!this.isRenderableValue(value)) {
      return 0;
    }

    return super.getBarHeight(value);
  }

  isRenderableBar(index: number): boolean {
    return this.renderableBarFlags[index] === true;
  }

  getBaselineBarIndex(): number {
    const baselineIndex = this.getFirstRenderableIndex();
    return baselineIndex >= 0 ? baselineIndex : 0;
  }

  getBaselineAnchorLeft(): string {
    const baselineIndex = this.getBaselineBarIndex();
    return `calc((var(--bar-width) / 2) + (${baselineIndex} * (var(--bar-width) + var(--bar-gap, 0px))))`;
  }

  override shouldShowConnector(): boolean {
    if (!super.shouldShowConnector()) {
      return false;
    }

    return this.renderableBarFlags.filter(Boolean).length > 1;
  }

  override getTopPadding(): number {
    if (!this.shouldShowConnector()) {
      return this.baseTopPadding;
    }

    if (!this.isFirstBarMode()) {
      return this.baseTopPadding + this.connectorHeaderClearance;
    }

    const baselineIndex = this.getBaselineBarIndex();
    const comparableCount = (Array.isArray(this.values) ? this.values : [])
      .map((_, index) => index)
      .filter((index) => index > baselineIndex && this.hasComparison(index)).length;
    const extraLevels = Math.max(0, comparableCount - 1);
    return this.baseTopPadding + this.connectorHeaderClearance + (extraLevels * this.connectorLevelStep);
  }

  override getConnectorBottom(): number {
    const values = Array.isArray(this.values) ? this.values : [];
    if (!values.length) {
      return super.getConnectorBottom();
    }

    const renderableHeights = values
      .map((value, index) => this.isRenderableBar(index) ? this.getDisplayBarHeight(index, value) : 0);
    const tallestBar = renderableHeights.length ? Math.max(...renderableHeights) : 0;
    return this.connectorLabelZoneHeight + this.connectorValueZoneHeight + tallestBar + this.customConnectorGap + this.getConnectorYOffsetPx();
  }

  override getBarTopBottom(index: number): number {
    if (index < 0 || index >= this.values.length) {
      return this.connectorLabelZoneHeight + this.connectorValueZoneHeight;
    }

    const value = Number(this.values[index] ?? 0);
    return this.connectorLabelZoneHeight + this.connectorValueZoneHeight + this.getDisplayBarHeight(index, value);
  }

  override getTipBottom(index: number): number {
    return this.getBarTopBottom(index) + this.connectorTipClearance;
  }

  override getFirstBarBranchBottom(index: number): number {
    const baselineIndex = this.getBaselineBarIndex();
    const levelFromClosest = Math.max(0, index - baselineIndex - 1);
    return this.getConnectorBottom() + (levelFromClosest * this.connectorLevelStep);
  }

  override getFirstBarBranchWidth(index: number): string {
    const baselineIndex = this.getBaselineBarIndex();
    const span = Math.max(0, index - baselineIndex);
    return `calc(${span} * (var(--bar-width) + var(--bar-gap, 0px)))`;
  }

  override getFirstBarTrunkHeight(): number {
    const baselineIndex = this.getBaselineBarIndex();
    const comparableIndexes = (Array.isArray(this.values) ? this.values : [])
      .map((_, index) => index)
      .filter((index) => index > baselineIndex && this.hasComparison(index));

    if (comparableIndexes.length === 0) {
      return 0;
    }

    const highestBranchBottom = this.getFirstBarBranchBottom(Math.max(...comparableIndexes));
    return Math.max(8, highestBranchBottom - this.getTipBottom(baselineIndex));
  }

  override getFirstBarRightStemHeight(index: number): number {
    if (!this.hasComparison(index)) {
      return 0;
    }

    return Math.max(8, this.getFirstBarBranchBottom(index) - this.getTipBottom(index));
  }

  private recomputeRenderableFlags(): void {
    const values = Array.isArray(this.values) ? this.values : [];
    this.renderableBarFlags = values.map((value) => this.isRenderableValue(value));
  }

  private isRenderableValue(value: any): boolean {
    if (value === null || value === undefined || value === '') {
      return false;
    }

    const numeric = Number(value);
    return Number.isFinite(numeric) && numeric !== 0;
  }

  private getFirstRenderableIndex(): number {
    return this.renderableBarFlags.findIndex((flag) => flag);
  }

  private applyFirstAvailableBaselineComparison(): void {
    if (!this.isFirstBarMode()) {
      return;
    }

    const values = Array.isArray(this.values) ? this.values : [];
    if (values.length <= 1) {
      return;
    }

    const baselineIndex = this.getFirstRenderableIndex();
    if (baselineIndex < 0) {
      this.comparisons = values.map(() => null);
      return;
    }

    const baseline = Number(values[baselineIndex]);
    this.comparisons = values.map((value, index) => {
      if (index === baselineIndex || !this.isRenderableBar(index)) {
        return null;
      }

      if (!Number.isFinite(baseline) || baseline === 0) {
        return null;
      }

      const current = Number(value);
      const percent = ((current - baseline) / baseline) * 100;
      return Number.isFinite(percent) ? Math.round(percent) : null;
    });
  }

  private isFormulaModeEnabled(): boolean {
    const raw = this.data?.enableCustomFormula;
    if (raw === undefined || raw === null) {
      return false;
    }

    if (typeof raw === 'string') {
      return raw.toLowerCase() === 'true';
    }

    return Boolean(raw);
  }

  private getFormulaTargetIndex(): number {
    const formula = this.data?.formula || {};
    const targetKey = this.pickFirstString(formula?.targetKey, this.data?.formulaTargetKey);
    if (targetKey) {
      const byKey = this.getIndexByKey(targetKey);
      if (byKey !== -1) {
        return byKey;
      }
    }

    const denominatorKey = this.pickFirstString(formula?.denominatorKey, this.data?.formulaDenominatorKey);
    if (denominatorKey) {
      const byKey = this.getIndexByKey(denominatorKey);
      if (byKey !== -1) {
        return byKey;
      }
    }

    const indexConfig = this.data?.formulaIndexes || {};
    if (Number.isInteger(indexConfig?.target)) {
      return indexConfig.target;
    }

    if (Number.isInteger(indexConfig?.d)) {
      return indexConfig.d;
    }

    return 3;
  }

  private resolveFormulaConfig(): { numeratorIndexes: number[]; denominatorIndex: number; targetIndex: number } {
    const formula = this.data?.formula || {};
    const numeratorKeys = this.toStringArray(formula?.numeratorKeys ?? this.data?.formulaNumeratorKeys);
    const denominatorKey = this.pickFirstString(formula?.denominatorKey, this.data?.formulaDenominatorKey);

    const numeratorIndexesFromKeys = numeratorKeys
      .map((key) => this.getIndexByKey(key))
      .filter((idx) => idx >= 0);
    const denominatorIndexFromKey = denominatorKey ? this.getIndexByKey(denominatorKey) : -1;

    if (numeratorIndexesFromKeys.length > 0 && denominatorIndexFromKey >= 0) {
      return {
        numeratorIndexes: numeratorIndexesFromKeys,
        denominatorIndex: denominatorIndexFromKey,
        targetIndex: this.getFormulaTargetIndex()
      };
    }

    const indexConfig = this.data?.formulaIndexes || {};
    const aIndex = Number.isInteger(indexConfig?.a) ? indexConfig.a : 0;
    const bIndex = Number.isInteger(indexConfig?.b) ? indexConfig.b : 1;
    const cIndex = Number.isInteger(indexConfig?.c) ? indexConfig.c : 2;
    const dIndex = Number.isInteger(indexConfig?.d) ? indexConfig.d : 3;

    return {
      numeratorIndexes: [aIndex, bIndex, cIndex],
      denominatorIndex: dIndex,
      targetIndex: this.getFormulaTargetIndex()
    };
  }

  private getIndexByKey(key: string): number {
    if (!Array.isArray(this.labels) || !key) {
      return -1;
    }

    const normalizedKey = `${key}`.trim().toLowerCase();
    return this.labels.findIndex((label: string) => `${label}`.trim().toLowerCase() === normalizedKey);
  }

  private toStringArray(value: any): string[] {
    if (!Array.isArray(value)) {
      return [];
    }

    return value
      .map((item: any) => `${item ?? ''}`.trim())
      .filter((item: string) => item.length > 0);
  }

  private pickFirstString(...values: any[]): string | null {
    for (const value of values) {
      if (typeof value === 'string' && value.trim().length > 0) {
        return value.trim();
      }
    }

    return null;
  }

  getEquationToken(): 'lcc' | 'fuel' | 'def' | 'lto' | 'total-tco' | 'none' {
    const label = this.getBottomLabel().toLowerCase();
    if (label.includes('lcc')) {
      return 'lcc';
    }
    if (label.includes('fuel')) {
      return 'fuel';
    }
    if (label.includes('def')) {
      return 'def';
    }
    if (label.includes('lto')) {
      return 'lto';
    }
    if (label.includes('total tco')) {
      return 'total-tco';
    }
    return 'none';
  }

  isEquationTopOperand(): boolean {
    const token = this.getEquationToken();
    return token === 'lcc' || token === 'fuel' || token === 'def';
  }

  isEquationBottomOperand(): boolean {
    return this.getEquationToken() === 'lto';
  }

  isEquationResult(): boolean {
    return this.getEquationToken() === 'total-tco';
  }

  shouldShowPlus(): boolean {
    const token = this.getEquationToken();
    return token === 'lcc' || token === 'lto';
  }

  shouldShowEquals(): boolean {
    return this.isEquationResult();
  }

  shouldShowDivider(): boolean {
    return this.isEquationBottomOperand();
  }

  override shouldShowComparisonBubbles(): boolean {
    const raw = this.data?.showComparisonBubbles ?? this.data?.chartConfig?.showComparisonBubbles;
    if (raw === undefined || raw === null) {
      return super.shouldShowComparisonBubbles();
    }

    if (typeof raw === 'string') {
      return raw.toLowerCase() !== 'false';
    }

    return Boolean(raw);
  }

  override getBottomLabel(): string {
    const label = `${this.data?.bottomLabel ?? this.data?.xAxisLabel ?? this.data?.axisLabel ?? ''}`.trim();
    const normalized = label.toLowerCase();
    const isEquationLabel = ['lcc', 'def', 'lto', 'fuel', 'total tco'].some((token) =>
      normalized.includes(token)
    );

    return isEquationLabel ? label : super.getBottomLabel();
  }

  private getFormulaFallbackValue(): number {
    const raw = Number(this.data?.formulaFallbackValue ?? this.data?.formula?.fallbackValue ?? 3);
    return Number.isFinite(raw) ? Math.round(raw) : 3;
  }

  private isFixedFormulaOutputEnabled(): boolean {
    const mode = `${this.data?.formulaOutputMode ?? this.data?.formula?.outputMode ?? ''}`.trim().toLowerCase();
    if (mode === 'fixed') {
      return true;
    }

    const raw = this.data?.forceFormulaOutputValue ?? this.data?.formula?.forceOutputValue;
    if (raw === undefined || raw === null) {
      return false;
    }

    if (typeof raw === 'string') {
      return raw.toLowerCase() === 'true';
    }

    return Boolean(raw);
  }

  shouldShowRawValueTooltip(): boolean {
    if (this.data?.chartConfig?.tooltip?.show === false || this.data?.tooltip === false) {
      return false;
    }

    const raw = this.data?.showRawValueTooltip;
    if (raw === undefined || raw === null) {
      return true;
    }

    if (typeof raw === 'string') {
      return raw.toLowerCase() !== 'false';
    }

    return Boolean(raw);
  }

  getRawValueTooltipItems(index?: number): Array<{ label: string; value: string }> {
    const labels = Array.isArray(this.labels) ? this.labels : [];
    const values = Array.isArray(this.values) ? this.values : [];
    const count = Math.min(labels.length, values.length);
    const items: Array<{ label: string; value: string }> = [];
    const showAllRows = this.shouldShowAllRawTooltipRows();

    const requestedIndex = Number(index);
    if (!showAllRows && Number.isInteger(index) && requestedIndex >= 0 && requestedIndex < count) {
      const barIndex = requestedIndex;
      const label = `${labels[barIndex] ?? `Bar ${barIndex + 1}`}`;
      const value = values[barIndex];

      if (this.hasStackedBar(barIndex)) {
        const segments = this.getStackedSegments(barIndex);
        const total = Number(segments[0] ?? value ?? 0);
        items.push({ label: `${label} Total`, value: this.formatRawValue(total) });
        items.push({ label: `${label} RebuildPrice`, value: this.formatRawValue(segments[1] ?? 0) });
        items.push({ label: `${label} Maintenance-Midlife`, value: this.formatRawValue(segments[2] ?? 0) });
        return items;
      }

      items.push({ label, value: this.formatRawValue(value) });
      return items;
    }

    for (let index = 0; index < count; index++) {
      const label = `${labels[index] ?? `Bar ${index + 1}`}`;
      const value = values[index];

      if (this.hasStackedBar(index)) {
        const segments = this.getStackedSegments(index);
        const total = Number(segments[0] ?? value ?? 0);
        items.push({ label: `${label} Total`, value: this.formatRawValue(total) });
        items.push({ label: `${label} RebuildPrice`, value: this.formatRawValue(segments[1] ?? 0) });
        items.push({ label: `${label} Maintenance-Midlife`, value: this.formatRawValue(segments[2] ?? 0) });
        continue;
      }

      items.push({
        label,
        value: this.formatRawValue(value)
      });
    }

    return items;
  }

  private shouldShowAllRawTooltipRows(): boolean {
    const raw = this.data?.showAllRawTooltipRows;
    if (raw === undefined || raw === null) {
      return false;
    }

    if (typeof raw === 'string') {
      return raw.toLowerCase() === 'true';
    }

    return Boolean(raw);
  }

  private formatRawValue(value: any): string {
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) {
      return `${value ?? 0}`;
    }

    return numeric.toLocaleString('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    });
  }

  getChartScale(): number {
    const parsed = Number(this.data?.chartScale ?? this.data?.chartConfig?.chartScale ?? this.defaultChartScale);
    if (!Number.isFinite(parsed) || parsed <= 0) {
      return this.defaultChartScale;
    }

    return parsed;
  }

  getChartOffsetXPx(): number {
    const parsed = Number(this.data?.chartOffsetX ?? this.data?.chartConfig?.chartOffsetX ?? 0);
    const offsetBase = Number.isFinite(parsed) ? parsed : 0;
    const token = this.getEquationToken();

    // Shift LCC and Fuel charts slightly right so split bars align cleanly with the plus symbol.
    if (token === 'fuel') {
      return offsetBase + 10;
    }

    if (token === 'lcc' && this.shouldUseSplitChartLayout()) {
      return offsetBase + 20;
    }

    return offsetBase;
  }

  getBarWidthPx(): number {
    const raw = Number(this.data?.barWidth ?? this.data?.chartConfig?.barWidth);
    if (Number.isFinite(raw) && raw > 0) {
      return raw;
    }

    // Use the same default width for all comparison charts, including split LCC charts.
    return 47;
  }

  getBarGapPx(): number {
    const parsed = Number(this.data?.barGap ?? this.data?.chartConfig?.barGap ?? this.defaultBarGap);
    if (!Number.isFinite(parsed) || parsed < 0) {
      return this.defaultBarGap;
    }

    return parsed;
  }

  getConnectorYOffsetPx(): number {
    const parsed = Number(this.data?.connectorYOffset ?? this.data?.chartConfig?.connectorYOffset ?? 0);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  shouldShowConnectorArrow(index: number): boolean {
    return index === this.getBaselineBarIndex();
  }

  shouldUseSplitChartLayout(): boolean {
    return this.splitComparisonCharts.length > 0;
  }

  getSplitBarWidthPx(): number {
    const raw = Number(this.data?.splitChartConfig?.barWidth ?? this.data?.splitBarWidth);
    if (Number.isFinite(raw) && raw > 0) {
      return raw;
    }

    return this.getBarWidthPx();
  }

  getSplitBarGapPx(): number {
    const raw = Number(this.data?.splitChartConfig?.barGap ?? this.data?.splitBarGap);
    if (Number.isFinite(raw) && raw >= 0) {
      return raw;
    }

    return this.getBarGapPx();
  }

  getSplitChartGapPx(): number {
    const raw = Number(this.data?.splitChartConfig?.chartGap ?? this.data?.splitChartGap ?? 12);
    if (Number.isFinite(raw) && raw >= 0) {
      return raw;
    }

    return 12;
  }

  shouldShowSplitChartConnector(chart: SplitComparisonChartView): boolean {
    if (!super.shouldShowConnector()) {
      return false;
    }

    return chart.renderableBarFlags.filter(Boolean).length > 1;
  }

  hasSplitChartComparison(chart: SplitComparisonChartView, index: number): boolean {
    const value = chart.comparisons[index];
    return typeof value === 'number' && Number.isFinite(value);
  }

  getSplitChartComparison(chart: SplitComparisonChartView, index: number): number | null {
    const value = chart.comparisons[index];
    return typeof value === 'number' && Number.isFinite(value) ? value : null;
  }

  getSplitChartBarColor(chart: SplitComparisonChartView, index: number): string {
    const sourceIndex = chart.sourceIndexes[index];
    return this.getBarColor(sourceIndex >= 0 ? sourceIndex : index);
  }

  getSplitChartLabel(chart: SplitComparisonChartView, index: number): string {
    const sourceIndex = chart.sourceIndexes[index];
    return `${this.labels[sourceIndex] ?? this.labels[index] ?? ''}`.trim();
  }

  isSplitChartRenderableBar(chart: SplitComparisonChartView, index: number): boolean {
    return chart.renderableBarFlags[index] === true;
  }

  getSplitChartBaselineIndex(chart: SplitComparisonChartView): number {
    return chart.baselineIndex >= 0 ? chart.baselineIndex : 0;
  }

  getSplitChartBaselineAnchorLeft(chart: SplitComparisonChartView): string {
    const baselineIndex = this.getSplitChartBaselineIndex(chart);
    return `calc((var(--bar-width) / 2) + (${baselineIndex} * (var(--bar-width) + var(--bar-gap, 0px))))`;
  }

  getSplitChartTopPadding(chart: SplitComparisonChartView): number {
    if (!this.shouldShowSplitChartConnector(chart)) {
      return this.baseTopPadding;
    }

    if (!this.isFirstBarMode()) {
      return this.baseTopPadding + this.connectorHeaderClearance;
    }

    const baselineIndex = this.getSplitChartBaselineIndex(chart);
    const comparableCount = chart.values
      .map((_, index) => index)
      .filter((index) => index > baselineIndex && this.hasSplitChartComparison(chart, index)).length;
    const extraLevels = Math.max(0, comparableCount - 1);
    return this.baseTopPadding + this.connectorHeaderClearance + (extraLevels * this.connectorLevelStep);
  }

  getSplitChartBarHeight(chart: SplitComparisonChartView, value: number): number {
    if (!Number.isFinite(value) || value <= 0 || chart.maxValue <= 0) {
      return 0;
    }

    return Math.max(8, (value / chart.maxValue) * this.maxBarHeight);
  }

  getSplitChartConnectorBottom(chart: SplitComparisonChartView): number {
    if (!chart.values.length) {
      return this.connectorLabelZoneHeight + this.connectorValueZoneHeight + this.customConnectorGap;
    }

    const tallestBar = Math.max(...chart.values.map((value) => this.getSplitChartBarHeight(chart, value)));
    return this.connectorLabelZoneHeight + this.connectorValueZoneHeight + tallestBar + this.customConnectorGap + this.getConnectorYOffsetPx();
  }

  getSplitChartBarTopBottom(chart: SplitComparisonChartView, index: number): number {
    if (index < 0 || index >= chart.values.length) {
      return this.connectorLabelZoneHeight + this.connectorValueZoneHeight;
    }

    const value = Number(chart.values[index] ?? 0);
    return this.connectorLabelZoneHeight + this.connectorValueZoneHeight + this.getSplitChartBarHeight(chart, value);
  }

  getSplitChartTipBottom(chart: SplitComparisonChartView, index: number): number {
    return this.getSplitChartBarTopBottom(chart, index) + this.connectorTipClearance;
  }

  getSplitChartTipHeight(chart: SplitComparisonChartView, index: number): number {
    return Math.max(8, this.getSplitChartConnectorBottom(chart) - this.getSplitChartTipBottom(chart, index));
  }

  getSplitChartFirstBarBranchBottom(chart: SplitComparisonChartView, index: number): number {
    const baselineIndex = this.getSplitChartBaselineIndex(chart);
    const levelFromClosest = Math.max(0, index - baselineIndex - 1);
    return this.getSplitChartConnectorBottom(chart) + (levelFromClosest * this.connectorLevelStep);
  }

  getSplitChartFirstBarBranchWidth(chart: SplitComparisonChartView, index: number): string {
    const baselineIndex = this.getSplitChartBaselineIndex(chart);
    const span = Math.max(0, index - baselineIndex);
    return `calc(${span} * (var(--bar-width) + var(--bar-gap, 0px)))`;
  }

  getSplitChartFirstBarTrunkHeight(chart: SplitComparisonChartView): number {
    const baselineIndex = this.getSplitChartBaselineIndex(chart);
    const comparableIndexes = chart.values
      .map((_, index) => index)
      .filter((index) => index > baselineIndex && this.hasSplitChartComparison(chart, index));

    if (comparableIndexes.length === 0) {
      return 0;
    }

    const highestBranchBottom = this.getSplitChartFirstBarBranchBottom(chart, Math.max(...comparableIndexes));
    return Math.max(8, highestBranchBottom - this.getSplitChartTipBottom(chart, baselineIndex));
  }

  getSplitChartFirstBarRightStemHeight(chart: SplitComparisonChartView, index: number): number {
    if (!this.hasSplitChartComparison(chart, index)) {
      return 0;
    }

    return Math.max(8, this.getSplitChartFirstBarBranchBottom(chart, index) - this.getSplitChartTipBottom(chart, index));
  }

  getSplitChartFirstBarBubbleBottom(): number {
    return -9;
  }

  getSplitChartBubbleOnConnectorBottom(chart: SplitComparisonChartView): number {
    return this.getSplitChartConnectorBottom(chart) - 9;
  }

  shouldShowSplitChartBaselineBubble(chart: SplitComparisonChartView, index: number): boolean {
    const raw = this.data?.showSplitChartBaselineBubble;
    const enabled = typeof raw === 'string' ? raw.toLowerCase() === 'true' : Boolean(raw);

    return enabled
      && this.shouldShowSplitChartConnector(chart)
      && this.isFirstBarMode()
      && index === this.getSplitChartBaselineIndex(chart)
      && this.isSplitChartRenderableBar(chart, index);
  }

  getSplitChartBaselineBubbleBottom(chart: SplitComparisonChartView, index: number): number {
    return this.getSplitChartTipBottom(chart, index) + 4;
  }

  shouldShowSplitChartConnectorArrow(chart: SplitComparisonChartView, index: number): boolean {
    return index === this.getSplitChartBaselineIndex(chart);
  }

  getSplitRawValueTooltipItems(chart: SplitComparisonChartView, index: number): Array<{ label: string; value: string }> {
    const sourceIndex = chart.sourceIndexes[index];
    const barLabel = `${this.labels[sourceIndex] ?? this.labels[index] ?? `Bar ${index + 1}`}`.trim();
    return [
      {
        label: `${barLabel} ${chart.label}`.trim(),
        value: this.formatRawValue(chart.values[index] ?? 0)
      }
    ];
  }

  hasStackedBar(index: number): boolean {
    return this.stackEnabledByBar[index] === true;
  }

  getStackedSegments(index: number): number[] {
    return this.stackSegmentsByBar[index] || [0, 0, 0];
  }

  getRenderableStackSegments(index: number): number[] {
    const segments = this.getStackedSegments(index);
    const total = Number(segments[0] || 0);
    const part1 = Number(segments[1] || 0);
    const part2 = Number(segments[2] || 0);
    const hasRebuildOrMaintenance = part1 > 0 || part2 > 0;

    if (hasRebuildOrMaintenance) {
      return [0, part1, part2];
    }

    return [Math.max(0, total), 0, 0];
  }

  getStackSegmentColor(barIndex: number, segmentIndex: number): string {
    const base = this.getBarColor(barIndex);
    const normalizedBase = this.normalizeHexColor(base)?.toLowerCase();
    const isRedFamily = normalizedBase === 'df2a21' || normalizedBase === 'c41e3a' || normalizedBase === 'ff0000';
    const rebuildShade = this.shadeHexColor(base, isRedFamily ? 0.18 : 0.12);
    const maintenanceShade = this.shadeHexColor(base, isRedFamily ? 0.62 : 0.48);
    const shades = [base, rebuildShade, maintenanceShade];
    return shades[segmentIndex % shades.length];
  }

  getStackedBarHeight(index: number): number {
    if (!this.hasStackedBar(index)) {
      return 0;
    }

    const value = Number((this.values || [])[index] ?? 0);
    return this.getBarHeight(value);
  }

  getStackSegmentHeight(barIndex: number, segmentIndex: number): number {
    const renderableSegments = this.getRenderableStackSegments(barIndex);
    const segmentValue = Number(renderableSegments[segmentIndex] || 0);
    const renderableTotal = renderableSegments.reduce((sum: number, value: number) => {
      const numeric = Number(value || 0);
      return sum + (Number.isFinite(numeric) ? numeric : 0);
    }, 0);

    if (!Number.isFinite(segmentValue) || segmentValue <= 0 || renderableTotal <= 0) {
      return 0;
    }

    const fullBarHeight = this.getStackedBarHeight(barIndex);
    const computedHeight = (segmentValue / renderableTotal) * fullBarHeight;
    const hasLabel = segmentIndex === 1 || segmentIndex === 2;
    const minHeight = hasLabel ? 10 : 2;
    return Math.max(minHeight, computedHeight);
  }

  shouldShowStackSegmentLabel(barIndex: number, segmentIndex: number): boolean {
    const label = this.getStackSegmentShortLabel(barIndex, segmentIndex);
    if (!label) {
      return false;
    }

    // Always show maintenance label when value exists, even for thin slices.
    if (segmentIndex === 2) {
      const sourceSegments = this.getStackedSegments(barIndex);
      return Number(sourceSegments[2] || 0) > 0;
    }

    return this.getStackSegmentHeight(barIndex, segmentIndex) >= 10;
  }

  getStackSegmentShortLabel(barIndex: number, segmentIndex: number): string {
    const sourceSegments = this.getStackedSegments(barIndex);
    const rebuildValue = Number(sourceSegments[1] || 0);
    const maintenanceValue = Number(sourceSegments[2] || 0);

    if (segmentIndex === 1 && rebuildValue > 0) {
      return 'R';
    }

    if (segmentIndex === 2 && maintenanceValue > 0) {
      return 'M';
    }

    return '';
  }

  private getDisplayBarHeight(index: number, value: number): number {
    if (this.hasStackedBar(index)) {
      return this.getStackedBarHeight(index);
    }

    return this.getBarHeight(value);
  }

  private prepareStackedSegments(source?: any): void {
    const sourceSegments = Array.isArray(source?.stackSegments) ? source.stackSegments : this.stackSegmentsByBar;
    const normalized = sourceSegments.map((entry: any) => this.normalizeStackSegments(entry));
    const sourceFlags = Array.isArray(source?.stackSegments)
      ? sourceSegments.map((entry: any) => Array.isArray(entry) && entry.length === 3)
      : this.stackEnabledByBar;

    const targetLength = Array.isArray(this.values) ? this.values.length : normalized.length;
    this.stackSegmentsByBar = Array.from({ length: targetLength }, (_, index) => normalized[index] || [0, 0, 0]);
    this.stackEnabledByBar = Array.from({ length: targetLength }, (_, index) => sourceFlags[index] === true);

    this.values = this.stackSegmentsByBar.map((segments: number[], index: number) => {
      if (!this.stackEnabledByBar[index]) {
        const current = Number((this.values || [])[index] ?? 0);
        return Number.isFinite(current) ? current : 0;
      }

      const total = Number(segments[0] || 0);
      const rebuild = Number(segments[1] || 0);
      const maintenance = Number(segments[2] || 0);
      const combined = rebuild + maintenance;

      if (combined > 0) {
        return combined;
      }

      return Number.isFinite(total) ? total : 0;
    });
    this.maxValue = this.values.length ? Math.max(...this.values) : 0;
  }

  private rebuildSplitComparisonCharts(): void {
    this.splitComparisonCharts = [];

    if (!this.isSplitStackModeEnabled()) {
      return;
    }

    if (!this.stackEnabledByBar.some((enabled) => enabled)) {
      return;
    }

    const definitions = this.resolveSplitChartDefinitions();
    const builtCharts = definitions.map((definition) => this.buildSplitComparisonChart(definition.label, definition.segmentIndex));
    
    // Filter out split charts that have no renderable values (all zero)
    this.splitComparisonCharts = builtCharts.filter((chart) => {
      const hasAnyRenderableValue = chart.renderableBarFlags.some((isRenderable) => isRenderable);
      return hasAnyRenderableValue;
    });
  }

  private normalizeStackSegments(entry: any): number[] {
    if (!Array.isArray(entry)) {
      return [0, 0, 0];
    }

    const safe = entry
      .slice(0, 3)
      .map((value: any) => Number(value))
      .map((value: number) => (Number.isFinite(value) && value > 0 ? value : 0));
    while (safe.length < 3) {
      safe.push(0);
    }

    return safe;
  }

  private loadRawStackData(filters?: any): void {
    if (!this.data?.apiUrl) {
      return;
    }

    this.customConfigService.getConfig(
      this.data.apiUrl,
      this.data,
      { ...(filters || {}), useQueryParams: this.data?.useQueryParams || false },
      this.data.method
    ).pipe(takeUntil(this.customDestroy$)).subscribe({
      next: (response: any) => {
        // Check if response contains error
        if (response?.error || !response?.responseList || (Array.isArray(response.responseList) && response.responseList.length === 0)) {
          // Clear data if error or empty response
          this.labels = [];
          this.values = [];
          this.prepareChart({ stackSegments: [] });
          return;
        }

        const parsed = this.extractRawStackData(response);
        if (!parsed.values.length) {
          return;
        }

        const signature = JSON.stringify(parsed);
        if (signature === this.lastRawStackSignature) {
          return;
        }

        this.lastRawStackSignature = signature;
        this.labels = parsed.labels;
        this.values = parsed.values;
        this.prepareChart({ stackSegments: parsed.stackSegments });
      },
      error: (err: any) => {
        // Gracefully handle API errors - just clear the data
        console.warn('[ComparisonBarChart] API error:', err);
        this.labels = [];
        this.values = [];
        this.prepareChart({ stackSegments: [] });
      }
    });
  }

  private extractRawStackData(response: any): { labels: string[]; values: number[]; stackSegments: number[][] } {
    const responsePath = this.data?.responsePath;
    const dataKey = this.data?.dataKey || 'groupData';
    const labelKey = this.data?.labelKey;
    const valueKey = this.data?.valueKey || 'count';
    const sortLabels = this.data?.sortLabels !== false;

    const baseData = responsePath ? this.getByPathForStack(response, responsePath) : this.resolveBaseDataForStack(response, dataKey);
    const groupData = baseData?.[dataKey] ?? baseData;

    this.barLabelsWithoutBreakdown.clear();

    if (Array.isArray(groupData)) {
      const labels = groupData.map((item: any, idx: number) => `${item?.[labelKey] ?? item?.label ?? idx + 1}`);
      
      // Check if first item's "CMI Actual" has all 3 values non-zero
      const cmiActualValue = groupData[0]?.['CMI Actual'] || groupData[0]?.['cmi_actual'];
      const shouldSplitAll = this.shouldEnableSplitForAllItems(cmiActualValue);
      
      const parsed = groupData.map((item: any, idx: number) => {
        const result = this.parseRawStackValue(item?.[valueKey] ?? item?.value ?? 0, shouldSplitAll);
        // Track bars without breakdown (empty segments)
        if (result.segments.length === 0) {
          this.barLabelsWithoutBreakdown.add(labels[idx]);
        }
        return result;
      });
      
      return {
        labels,
        values: parsed.map((entry) => entry.value),
        stackSegments: parsed.map((entry) => entry.segments)
      };
    }

    if (groupData && typeof groupData === 'object') {
      let orderedKeys = Object.keys(groupData);
      if (sortLabels) {
        orderedKeys = orderedKeys.sort((a, b) => Number(a) - Number(b));
      }

      // Check if "CMI Actual" has all 3 values non-zero
      const cmiActualValue = groupData['CMI Actual'] || groupData['cmi_actual'];
      const shouldSplitAll = this.shouldEnableSplitForAllItems(cmiActualValue);

      const labels = orderedKeys.map((key) => {
        const record = groupData[key];
        return `${record?.[labelKey] ?? key}`;
      });
      const parsed = orderedKeys.map((key, idx) => {
        const record = groupData[key];
        const raw = (record !== null && typeof record === 'object' && !Array.isArray(record))
          ? (record?.[valueKey] ?? record?.value ?? 0)
          : record;
        const result = this.parseRawStackValue(raw, shouldSplitAll);
        // Track bars without breakdown (empty segments)
        if (result.segments.length === 0) {
          this.barLabelsWithoutBreakdown.add(labels[idx]);
        }
        return result;
      });

      return {
        labels,
        values: parsed.map((entry) => entry.value),
        stackSegments: parsed.map((entry) => entry.segments)
      };
    }

    return { labels: [], values: [], stackSegments: [] };
  }

  private isSplitStackModeEnabled(): boolean {
    const raw = this.data?.renderStackAsSplitCharts ?? this.data?.splitStackedComparisons;
    const explicit = typeof raw === 'string'
      ? raw.toLowerCase() === 'true'
      : Boolean(raw);

    return explicit || this.shouldAutoSplitLccStackMode();
  }

  private shouldAutoSplitLccStackMode(): boolean {
    return this.getEquationToken() === 'lcc' && this.stackEnabledByBar.some((enabled) => enabled);
  }

  private resolveSplitChartDefinitions(): Array<{ label: string; segmentIndex: number }> {
    const configured = Array.isArray(this.data?.splitStackCharts) ? this.data.splitStackCharts : [];
    const normalized = configured
      .map((item: any) => ({
        label: `${item?.label ?? ''}`.trim(),
        segmentIndex: Number(item?.segmentIndex)
      }))
      .filter((item: { label: string; segmentIndex: number }) =>
        item.label.length > 0 && Number.isInteger(item.segmentIndex) && item.segmentIndex > 0
      );

    if (normalized.length > 0) {
      return normalized;
    }

    return [
      { label: 'Rebuild', segmentIndex: 1 },
      { label: 'Maintenance', segmentIndex: 2 }
    ];
  }

  private buildSplitComparisonChart(label: string, segmentIndex: number): SplitComparisonChartView {
    const rawValues = this.stackSegmentsByBar.map((segments: number[], index: number) => {
      const rawSegmentValue = Number(segments[segmentIndex] ?? NaN);
      const fallbackValue = Number((this.values || [])[index] ?? 0);
      const hasStackBreakdown = this.stackEnabledByBar[index] === true;
      // For bars with breakdown, keep true segment value (including 0) and avoid falling back to total.
      const value = hasStackBreakdown
        ? (Number.isFinite(rawSegmentValue) ? rawSegmentValue : 0)
        : fallbackValue;
      return Number.isFinite(value) && value > 0 ? value : 0;
    });
    const sourceIndexes = rawValues.map((_, index) => index);
    const values = [...rawValues];
    const renderableBarFlags = values.map((value) => this.isRenderableValue(value));
    const baselineIndex = renderableBarFlags.findIndex((isRenderable) => isRenderable);
    const baseline = baselineIndex >= 0 ? Number(values[baselineIndex]) : Number.NaN;
    const comparisons = values.map((value, index) => {
      if (index === baselineIndex || !renderableBarFlags[index]) {
        return null;
      }

      if (!Number.isFinite(baseline) || baseline === 0) {
        return null;
      }

      const percent = ((value - baseline) / baseline) * 100;
      return Number.isFinite(percent) ? Math.round(percent) : null;
    });
    const maxValue = renderableBarFlags.some((isRenderable) => isRenderable) ? Math.max(...values) : 0;

    return {
      key: `${label}-${segmentIndex}`.toLowerCase().replace(/\s+/g, '-'),
      label,
      sourceIndexes,
      values,
      comparisons,
      renderableBarFlags,
      maxValue,
      baselineIndex
    };
  }

  private parseRawStackValue(rawValue: any, shouldForceSplit?: boolean): { value: number; segments: number[] } {
    if (Array.isArray(rawValue)) {
      const normalized = this.normalizeStackSegments(rawValue);
      const total = Number(normalized[0] || (normalized[1] + normalized[2]) || 0);
      return {
        value: total,
        segments: normalized
      };
    }

    if (rawValue && typeof rawValue === 'object') {
      const total = this.pickNumericValue(rawValue, [
        'total',
        'value',
        'count',
        'lcc_total',
        'total_lcc',
        'overall'
      ]);
      const rebuild = this.pickNumericValue(rawValue, [
        'rebuild',
        'rebuildPrice',
        'rebuild_price',
        'rebuildprice',
        'rebuildCost',
        'rebuild_cost'
      ]);
      const maintenance = this.pickNumericValue(rawValue, [
        'maintenance',
        'maintenanceMidlife',
        'maintenance_midlife',
        'maintenanceMidLife',
        'maintenance-midlife',
        'midlife',
        'mid_life',
        'midLife'
      ]);

      if (rebuild > 0 || maintenance > 0 || total > 0) {
        const resolvedTotal = total > 0 ? total : rebuild + maintenance;
        return {
          value: resolvedTotal,
          segments: [resolvedTotal, rebuild, maintenance]
        };
      }
    }

    if (typeof rawValue === 'string' && rawValue.includes(',')) {
      const parts = rawValue
        .split(',')
        .map((part) => Number(part.trim()))
        .filter((value) => Number.isFinite(value)) as number[];

      if (parts.length > 0) {
        // Determine if segments should be included based on shouldForceSplit flag
        let hasNonZeroSegments = false;
        if (shouldForceSplit !== undefined) {
          // If flag is explicitly set, use it
          hasNonZeroSegments = shouldForceSplit;
        } else {
          // Otherwise, check if the 2nd and 3rd values are not zero
          hasNonZeroSegments = parts.length >= 3 && (Math.abs(parts[1]) > 0.01 || Math.abs(parts[2]) > 0.01);
        }
        
        return {
          value: parts[0],
          segments: hasNonZeroSegments ? parts.slice(0, 3) : []
        };
      }
    }

    const numeric = Number(rawValue);
    return {
      value: Number.isFinite(numeric) ? numeric : 0,
      segments: []
    };
  }

  private shouldEnableSplitForAllItems(cmiActualValue: any): boolean {
    if (!cmiActualValue) {
      return false;
    }

    if (typeof cmiActualValue === 'string' && cmiActualValue.includes(',')) {
      const parts = cmiActualValue
        .split(',')
        .map((part) => Number(part.trim()))
        .filter((value) => Number.isFinite(value)) as number[];

      // Only split if the first 3 values are all non-zero
      if (parts.length >= 3) {
        return parts.slice(0, 3).every((v) => Math.abs(v) > 0.01);
      }
    }

    return false;
  }

  hasNoBreakdown(label: string): boolean {
    return this.barLabelsWithoutBreakdown.has(label);
  }

  shouldShowNoBreakdownChartNote(): boolean {
    if (this.shouldUseSplitChartLayout()) {
      return false;
    }

    if (this.getEquationToken() !== 'lcc') {
      return false;
    }

    if (!this.shouldShowLccNoBreakdownNoteForCurrentMine()) {
      return false;
    }

    return this.barLabelsWithoutBreakdown.size > 0;
  }

  private shouldShowLccNoBreakdownNoteForCurrentMine(): boolean {
    const mineName = this.resolveCurrentMineName();
    if (!mineName) {
      return false;
    }

    return this.lccNoBreakdownMineWhitelist.has(mineName);
  }

  private resolveCurrentMineName(): string {
    const navState = (window?.history?.state || {}) as any;
    const filters = this.customGlobalFilterService.getCurrentFilters?.() || {};
    const requestParams = filters?.requestParams || {};
    const queryParams = filters?.queryParams || {};

    const rawMine = this.pickFirstString(
      requestParams?.mine,
      requestParams?.mine_site,
      queryParams?.mine,
      queryParams?.mine_site,
      filters?.mine,
      filters?.mine_site,
      navState?.selectedMineName,
      navState?.mine_site,
      navState?.selectedMine?.name,
      this.data?.customPayload?.mine,
      this.data?.customPayload?.mine_site
    );

    if (!rawMine) {
      return '';
    }

    return rawMine.trim().toLowerCase();
  }

  private pickNumericValue(source: Record<string, any>, keys: string[]): number {
    for (const key of keys) {
      const value = source?.[key];
      const numeric = Number(value);
      if (Number.isFinite(numeric) && numeric > 0) {
        return numeric;
      }
    }

    return 0;
  }

  private resolveBaseDataForStack(response: any, dataKey: string): any {
    if (!response || typeof response !== 'object') {
      return response;
    }

    const candidatePaths = [
      '',
      'data',
      'data.0',
      'responseData',
      'responseData.0',
      'responseList.0',
      'responseList.0.data',
      'responseList.0.data.0',
      'responseList.0.responseData',
      'responseList.0.responseData.0',
      'ui-data.0.data'
    ];

    for (const path of candidatePaths) {
      const candidate = path ? this.getByPathForStack(response, path) : response;
      if (!candidate || typeof candidate !== 'object') {
        continue;
      }

      if (candidate[dataKey] !== undefined) {
        return candidate;
      }

      if (Array.isArray(candidate.category) && Array.isArray(candidate.value)) {
        return candidate;
      }
    }

    return response;
  }

  private getByPathForStack(source: any, path: string): any {
    return path.split('.').reduce((acc: any, key: string) => {
      if (acc === null || acc === undefined) {
        return undefined;
      }

      if (/^\d+$/.test(key)) {
        return acc[Number(key)];
      }

      return acc[key];
    }, source);
  }

  private shadeHexColor(color: string, percent: number): string {
    const hex = this.normalizeHexColor(color);
    if (!hex) {
      return color;
    }

    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);

    const t = percent < 0 ? 0 : 255;
    const p = Math.abs(percent);
    const nr = Math.round((t - r) * p + r);
    const ng = Math.round((t - g) * p + g);
    const nb = Math.round((t - b) * p + b);

    return `#${nr.toString(16).padStart(2, '0')}${ng.toString(16).padStart(2, '0')}${nb.toString(16).padStart(2, '0')}`;
  }

  private normalizeHexColor(color: string): string | null {
    if (typeof color !== 'string') {
      return null;
    }

    const raw = color.trim().replace('#', '');
    if (/^[0-9a-fA-F]{6}$/.test(raw)) {
      return raw;
    }

    if (/^[0-9a-fA-F]{3}$/.test(raw)) {
      return raw.split('').map((c) => c + c).join('');
    }

    return null;
  }
}
